import { Input } from '@/components/controls/Input'
import { Checkbox } from '@/components/controls/Checkbox'
import { Textarea } from '@/components/controls/Textarea'
import { Button } from '@/components/controls/Button'
import { useForm } from 'react-hook-form'
import { useRef, useState } from 'react'
import { ContactFormService } from '@/services/contact-form'
import { CaptchaService } from '@/services/captcha'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { emitter } from '@/utils/emitter'
import { handleResponse } from '@/utils/http'
import Link from 'next/link'

interface Payload {
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  message: string,
  sendOffers: boolean
}

export const ContactForm: React.FC = () => {
  const [isProcessing, setProcessing] = useState(false)
  const captchaToken = useRef('')
  const captchaRef= useRef<HCaptcha>(null)
  const { register, handleSubmit, reset, formState: {errors, touchedFields}} = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
      sendOffers: true
    }
  })

  const onSubmit = async (data: Payload) => {
    if (isProcessing) return
    if (!captchaToken.current) return

    setProcessing(true)

    const captchaRes = await CaptchaService.verify(captchaToken.current)

    if (captchaRes.isValid) {
      emitter.emit('contact-form-submitted', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        message: data.message,
        sendOffers: !!data.sendOffers
      })

      const res = await ContactFormService.send(
        data.firstName,
        data.lastName,
        data.email,
        data.phone,
        data.message
      )

      handleResponse(res, 'We got your email! Will contact soon, cheers.')
        .then(() => {
          resetFrom()
        })
    } else {
      handleResponse(captchaRes)
        .then(() => {
          resetFrom()
        })
    }

    setProcessing(false)
  }

  const resetFrom = () => {
    captchaRef.current?.resetCaptcha()
    reset({}, {
      keepDefaultValues: true
    })
  }

  return (
    <form id="contact-form" className="sm2:w-[80%] xl:w-auto xxl:w-[80%] mx-auto" onSubmit={handleSubmit(onSubmit)}>
      <Input
        label="First Name"
        placeholder="Bruce"
        dark={true}
        valid={!errors.firstName && touchedFields.firstName} error={errors.firstName?.message}
        {...register('firstName', { required: 'First Name required' })}
      />
      <Input
        label="Last Name"
        placeholder="Wayne"
        dark={true}
        valid={!errors.lastName && touchedFields.lastName} error={errors.lastName?.message}
        {...register('lastName', { required: 'Last Name required' })}
      />
      <Input
        label="Email"
        placeholder="bruce@thebatcave.com"
        dark={true}
        valid={!errors.email && touchedFields.email} error={errors.email?.message}
        {...register('email', {
          required: 'Email required',
          pattern: {
            value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            message: 'This email is invalid'
          }
        })}
      />
      <Input
        label="Phone"
        placeholder="Your Phone"
        dark={true}
        {...register('phone')}
      />
      <Textarea
        label="Message"
        valid={!errors.message && touchedFields.message}
        error={errors.message?.message}
        {...register('message', { required: 'Field required' })}
      />
      <div className="mb-16-to-20">
        <HCaptcha
          ref={captchaRef}
          sitekey={process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY as string}
          theme="dark"
          onVerify={(token) => {
            captchaToken.current = token
          }}
        />
      </div>
      <Checkbox
        label="Send me occasional offers &amp; interesting content."
        value={'true'}
        {...register('sendOffers')}
      />
      <div className="flex justify-center mt-[25px]">
        <Button type="primary" disabled={isProcessing} isSubmit={true} maxWidth={300} fullWidth={true}>Submit</Button>
      </div>
      <p className='text-[12px] mt-[30px] font--accent opacity-[0.8]'>By clicking continue, I agree to the terms and conditions and to Commit Action using my contact details to get in touch. No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. For help, reply HELP. To opt-out, reply STOP. Msg&data rates may apply. Frequency varies.<br/><Link href={'/privacy-policy'}><a className='link'>Our Privacy Policy</a></Link></p>
    </form>
  )
}
